import { useEffect, useState } from 'react';
import { Modal } from '../ui/modal.jsx';
import { api, ApiError } from '../../api/index.js';
import { useToast } from '../ui/toast.jsx';

export function ChangePasswordModal({ open, onClose }) {
  const toast = useToast();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setCurrent('');
    setNext('');
    setConfirm('');
    setBusy(false);
    setError(null);
  }, [open]);

  const tooShort = next.length > 0 && next.length < 12;
  const mismatch = confirm.length > 0 && next !== confirm;
  const sameAsOld = next.length > 0 && next === current;
  const canSubmit =
    current.length > 0 &&
    next.length >= 12 &&
    next.length <= 128 &&
    next === confirm &&
    !sameAsOld &&
    !busy;

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await api.changeOwnPassword(current, next);
      toast.success('Password changed. Other sessions have been signed out.');
      onClose?.();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.payload?.error || err.message : err?.message || 'Operation failed.',
      );
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title="Change your password"
      description="Your current session stays valid. Other active sessions are signed out immediately."
      size="sm"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-secondary" disabled={busy} data-skip-autofocus>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={!canSubmit} className="btn-primary">
            {busy ? (
              <>
                <span className="loader-spinner" aria-hidden="true" /> Working…
              </>
            ) : (
              <>
                <i className="fa-solid fa-key" aria-hidden="true" /> Change password
              </>
            )}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && (
          <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-soft px-4 py-3 text-sm text-rose-700">
            <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
            {error}
          </div>
        )}
        <div>
          <label htmlFor="cpw-current" className="field-label">Current password</label>
          <input
            id="cpw-current"
            type="password"
            className="field"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            autoComplete="current-password"
            disabled={busy}
          />
        </div>
        <div>
          <label htmlFor="cpw-new" className="field-label">New password</label>
          <input
            id="cpw-new"
            type="password"
            className="field"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            placeholder="Min. 12 characters"
            autoComplete="new-password"
            disabled={busy}
          />
          {tooShort && <p className="mt-1 text-xs text-rose-600">Password must be at least 12 characters.</p>}
          {sameAsOld && <p className="mt-1 text-xs text-rose-600">New password must differ from current.</p>}
        </div>
        <div>
          <label htmlFor="cpw-confirm" className="field-label">Confirm new password</label>
          <input
            id="cpw-confirm"
            type="password"
            className="field"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
            disabled={busy}
          />
          {mismatch && <p className="mt-1 text-xs text-rose-600">Passwords do not match.</p>}
        </div>
      </form>
    </Modal>
  );
}
